import { useMemo, useState } from "react";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { useTheme } from "@/context/ThemeContext";
import { useApi } from "@/hooks/use-api";
import { downloadFile } from "@/lib/api";
import { PageHeader, PanelHead } from "@/components/common/PageHeader";
import { KpiCard, KpiStrip } from "@/components/common/KpiCard";
import { ErrorState } from "@/components/common/EmptyState";
import { TimeRangePicker, rangeToParams } from "@/components/common/TimeRangePicker";
import { UtilBar } from "@/components/common/UtilBar";
import { DailyEnergyChart } from "@/components/charts/SimpleCharts";
import { fmtDayTime, fmtHours, fmtInt, fmtMWh, fmtNOK, fmtNum, fmtPct, fmtPrice } from "@/lib/format";
import { useNavigate } from "react-router-dom";

const REPORT_RANGES = [
  { key: "7d", label: "7 d" },
  { key: "30d", label: "30 d" },
  { key: "90d", label: "90 d" },
  { key: "ytd", label: "Hittil i år" },
  { key: "365d", label: "1 år" },
];

export default function ReportsPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [range, setRange] = useState({ range: "30d" });
  const [plantId, setPlantId] = useState("all");
  const [resolution, setResolution] = useState("day");
  const [exporting, setExporting] = useState(null);
  const params = useMemo(() => ({ ...rangeToParams(range), plant_id: plantId === "all" ? undefined : plantId }), [range, plantId]);
  const { data, isLoading, isError, refetch, isFetching } = useApi("/reports/production", params, { keepPrevious: true });
  const plants = useApi("/plants");

  const doExport = async (format) => {
    setExporting(format);
    try {
      const name = await downloadFile("/reports/export", { format, resolution, ...params });
      toast.success("Eksport klar", { description: name });
    } catch (err) {
      toast.error(err.message || "Eksport feilet");
    } finally {
      setExporting(null);
    }
  };

  if (isError) return <ErrorState message="Kunne ikke hente rapporten." onRetry={refetch} />;
  const t = data?.totals;
  const maxEnergy = data ? Math.max(...data.per_plant.map((r) => r.energy_mwh), 1) : 1;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <PageHeader
        eyebrow="Rapporter"
        title="Produksjonsrapport"
        meta={data ? `${fmtDayTime(data.start)} – ${fmtDayTime(data.end)} · ${data.plant_count} kraftverk i utvalget` : "Henter …"}
        actions={
          <>
            <Select value={resolution} onValueChange={setResolution}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="report-resolution">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="day">Døgnverdier</SelectItem>
                <SelectItem value="hour">Timeverdier</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => doExport("csv")} disabled={!!exporting} data-testid="report-export-csv">
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <FileText className="h-3.5 w-3.5" aria-hidden="true" />} CSV
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => doExport("xlsx")} disabled={!!exporting} data-testid="report-export-xlsx">
              {exporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden="true" />} Excel
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <TimeRangePicker value={range} onChange={setRange} options={REPORT_RANGES} testIdPrefix="report-range" />
        <Select value={plantId} onValueChange={setPlantId}>
          <SelectTrigger className="h-8 w-full text-xs md:w-[260px]" data-testid="report-plant-select">
            <SelectValue placeholder="Alle kraftverk" />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem value="all">Alle kraftverk</SelectItem>
            {(plants.data?.plants || []).map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <KpiStrip cols={4}>
        <KpiCard label="Produksjon" value={t ? fmtMWh(t.energy_mwh).split(" ")[0] : ""} unit={t ? fmtMWh(t.energy_mwh).split(" ")[1] : ""} sub={t ? `${fmtInt(t.hours)} timer i perioden` : ""} loading={isLoading} testId="report-kpi-energy" />
        <KpiCard label="Estimert inntekt" value={t ? fmtNOK(t.revenue_nok).replace(" mill. kr", "").replace(" kr", "") : ""} unit={t ? (fmtNOK(t.revenue_nok).includes("mill.") ? "mill. kr" : "kr") : ""} sub={t ? `Snittpris ${fmtPrice(t.avg_spot_nok_mwh)}` : ""} loading={isLoading} testId="report-kpi-revenue" />
        <KpiCard label="Kapasitetsfaktor" value={t ? fmtNum(t.capacity_factor_pct, 1) : ""} unit="%" sub="av installert effekt" loading={isLoading} testId="report-kpi-cf" />
        <KpiCard label="Timer stanset" value={t ? fmtInt(t.hours_stopped) : ""} unit="t" sub={t ? `${fmtNum((100 * t.hours_running) / Math.max(t.hours, 1), 1)} % tilgjengelighet` : ""} loading={isLoading} testId="report-kpi-stopped" />
      </KpiStrip>

      <section className="panel">
        <PanelHead title="Produksjon per døgn" description={data ? `${data.plant_count} kraftverk i utvalget` : ""} />
        <div className="px-3 pb-3 pt-4 sm:px-5">{isLoading ? <Skeleton className="h-[240px] w-full" /> : <DailyEnergyChart rows={data.per_day} isDark={isDark} height={240} testId="report-daily-chart" />}</div>
      </section>

      <section className="panel">
        <PanelHead title="Per kraftverk" description="Sortert etter produksjon i perioden" />
        <div className={isFetching ? "opacity-70 transition-opacity" : ""}>
          {isLoading ? (
            <Skeleton className="m-5 h-64" />
          ) : (
            <Table data-testid="report-plant-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Kraftverk</TableHead>
                  <TableHead className="hidden text-right lg:table-cell">Installert</TableHead>
                  <TableHead className="w-[260px]">Produksjon</TableHead>
                  <TableHead className="text-right">Kap.faktor</TableHead>
                  <TableHead className="hidden text-right md:table-cell">Tilgj.</TableHead>
                  <TableHead className="hidden text-right md:table-cell">Stanset</TableHead>
                  <TableHead className="hidden text-right xl:table-cell">Snittpris</TableHead>
                  <TableHead className="pr-5 text-right">Est. inntekt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_plant.map((r) => (
                  <TableRow key={r.plant_id} className="group cursor-pointer" onClick={() => navigate(`/kraftverk/${r.plant_id}`)} data-testid={`report-row-${r.plant_id}`}>
                    <TableCell className="pl-5 font-medium">
                      <span className="transition-colors group-hover:text-primary">{r.name}</span>
                      <div className="mono text-[10.5px] text-muted-foreground">{r.price_area}</div>
                    </TableCell>
                    <TableCell className="metric hidden text-right text-xs lg:table-cell">{fmtNum(r.capacity_mw, 1)} MW</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="util-track">
                          <div className="util-fill bg-primary" style={{ width: `${(100 * r.energy_mwh) / maxEnergy}%` }} />
                        </div>
                        <span className="metric w-24 shrink-0 text-right text-[13px] font-medium">{fmtMWh(r.energy_mwh)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="metric text-right text-xs">{fmtPct(r.capacity_factor_pct, 0)}</TableCell>
                    <TableCell className="metric hidden text-right text-xs md:table-cell">{fmtPct(r.availability_pct, 1)}</TableCell>
                    <TableCell className="metric hidden text-right text-xs md:table-cell">{fmtHours(r.hours_stopped)}</TableCell>
                    <TableCell className="metric hidden text-right text-xs xl:table-cell">{fmtPrice(r.avg_price_nok_mwh)}</TableCell>
                    <TableCell className="metric pr-5 text-right text-xs">{fmtNOK(r.revenue_nok)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <p className="border-t border-border px-5 py-3 text-[11px] leading-relaxed text-muted-foreground">Inntekt er estimert som timeproduksjon × spotpris i prisområdet, uten nettleie, avgifter eller fastprisavtaler. Et kunde-API for maskinell uthenting kan tilbys på samme datagrunnlag.</p>
      </section>
    </div>
  );
}
